import React, { useEffect, useState } from 'react'
import API from '../../../../../services/API'

import Autocomplete from '@mui/material/Autocomplete'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import AddCircle from '@mui/icons-material/AddCircle'
import BigModal from '../../../../../components/Modals/BigModal'
import { Button, Stack, Typography } from '@mui/material'
import { Edit } from '@mui/icons-material'

const Popup = ({ open, onClose, type, existingDestination, onEditSubmit }) => {
  const [destinationName, setDestinationName] = useState()
  const [destinationType, setDestinationType] = useState([type])
  const [publicPhone, setPublicPhone] = useState('')
  const [privatePhone, setPrivatePhone] = useState('')
  const [address, setAddress] = useState('')
  const [what3words, setWhat3words] = useState('')
  const [accessInfo, setAccessInfo] = useState('')

  useEffect(() => {
    if (!open) return

    setDestinationName(existingDestination?.name ?? '')
    setDestinationType(existingDestination?.type ?? (type ? [type] : []))
    setPublicPhone(existingDestination?.publicPhone ?? '')
    setPrivatePhone(existingDestination?.privatePhone ?? '')
    setAddress(existingDestination?.address ?? '')
    setWhat3words(existingDestination?.what3words ?? '')
    setAccessInfo(existingDestination?.accessInfo ?? '')
  }, [open, existingDestination, type])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (existingDestination) {
      API.put(`/eventplanning/hospitals/${existingDestination._id}`, {
        name: destinationName,
        type: destinationType,
        publicPhone,
        privatePhone,
        address,
        what3words,
        accessInfo,
      }).then((res) => res.status === 200 && onClose())
    } else {
      API.post('/eventplanning/hospitals', {
        name: destinationName,
        type: destinationType,
        publicPhone,
        privatePhone,
        address,
        what3words,
        accessInfo,
      }).then((res) => res.status === 200 && onClose())
    }
    onEditSubmit(() => ({
      name: destinationName,
      type: destinationType,
      publicPhone,
      privatePhone,
      address,
      what3words,
      accessInfo,
    }))
  }

  return (
    <BigModal open={open} onClose={onClose} autoWidth>
      <Stack gap={1} component="form" onSubmit={handleSubmit}>
        <Typography variant="title3" textAlign="center">
          {existingDestination ? 'Edit' : 'Add a new'} Destination
        </Typography>
        <TextField
          label="Destination Name"
          value={destinationName}
          onChange={(e) => setDestinationName(e.target.value)}
          autoComplete="off"
          name="addhospitalname"
          required
        />
        <Autocomplete
          options={[
            'Emergency Department',
            'PPCI',
            'Stroke',
            'Trauma Unit',
            'Major Trauma Centre',
            'Minor Injury Unit',
            'Paediatrics',
            'Paediatric MTC',
          ]}
          value={destinationType}
          multiple
          onChange={(e, newValue) => setDestinationType(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type"
              inputProps={{
                ...params.inputProps,
                required: destinationType.length === 0,
              }}
            />
          )}
        />
        {/* PHONE NUMBERS */}
        <TextField
          label="Public Phone Number"
          required
          value={publicPhone}
          onChange={(e) => setPublicPhone(e.target.value)}
          helperText="For public use"
          autoComplete="off"
          name="addhospital-publicphone"
        />
        <TextField
          label="Private Phone Number"
          required
          value={privatePhone}
          onChange={(e) => setPrivatePhone(e.target.value)}
          helperText="Not for public use"
          error
          autoComplete="off"
          name="addhospital-privatephone"
        />

        <TextField
          label="Address"
          required
          fullWidth
          onChange={(e) => setAddress(e.target.value)}
          value={address}
          autoComplete="off"
          name="addhospitaladdress"
        />
        <TextField
          label="What3Words"
          value={what3words}
          onChange={(e) => setWhat3words(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">///</InputAdornment>
            ),
          }}
          autoComplete="off"
          name="newpopupwhat3words"
        />
        <TextField
          value={accessInfo}
          onChange={(e) => setAccessInfo(e.target.value)}
          multiline
          minRows={2}
          label="Access Information / Notes"
          autoComplete="off"
        />
        <Button variant="contained" color="success" size="small" type="submit">
          Submit
        </Button>
      </Stack>
    </BigModal>
  )
}

const DestinationPicker = ({
  value,
  onChange,
  type,
  label,
  destinationOptions,
  loadOptions,
  onEditSubmit,
}) => {
  const [openAddNew, setOpenAddNew] = useState(false)
  const [editDestination, setEditDestination] = useState(null)

  return (
    <>
      <Autocomplete
        value={value}
        options={destinationOptions}
        isOptionEqualToValue={(opt, val) => {
          return opt._id === val._id
        }}
        onChange={onChange}
        getOptionLabel={(opt) => opt.name}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}

                  <InputAdornment
                    position="end"
                    className="custom-autocomplete-adornment"
                  >
                    {!value ? (
                      <Tooltip title="Add a new Destination">
                        <IconButton
                          size="small"
                          onClick={() => setOpenAddNew(true)}
                        >
                          <AddCircle color="primary" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={`Edit ${value?.name ?? ''}`}>
                        <IconButton
                          size="small"
                          onClick={() => setEditDestination(value)}
                        >
                          <Edit color="primary" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </InputAdornment>
                </>
              ),
            }}
          />
        )}
        sx={{
          '& .custom-autocomplete-adornment': {
            opacity: 0,
            transition: 'opacity 150ms ease-in-out',
          },
          '&:hover .custom-autocomplete-adornment': {
            opacity: 1,
          },
          '&.Mui-focused .custom-autocomplete-adornment': {
            opacity: 1,
          },
        }}
      />
      <Popup
        open={openAddNew || !!editDestination}
        existingDestination={editDestination}
        type={type}
        onEditSubmit={onEditSubmit}
        onClose={() => {
          loadOptions()
          setOpenAddNew(false)
          setEditDestination(null)
        }}
      />
    </>
  )
}

export default DestinationPicker
